// core/offline/outbox-processor.service.ts
import { Injectable } from '@angular/core';
import { HttpClient, HttpContext, HttpHeaders } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { REPLAYING, OUTBOX_ID, OFFLINE_META, CLIENT_ID } from './offline-context';
import { db, outboxRepo, deepReplaceIds, entityRepo } from './offline-database';

@Injectable({ providedIn: 'root' })
export class OutboxProcessorService {
    private running = false;
    private ticker: any;

    constructor(private http: HttpClient) {
        // beim Start & beim Online-Change selber triggern (machst du in deinem NetworkStatusService)
        window.addEventListener('online', () => this.drain());
        // falls online & Outbox nicht leer: alle 10s erneut versuchen (robust gegen Edge-Cases)
        this.ticker = setInterval(() => { if (navigator.onLine) this.drain(); }, 10000);
    }

    private async _hydrateSidCache(collection: string, scopeKey: string) {
        this._sidCache.clear();

        // Primär: Mapping aus idmap (das schreibst du in replaceClientWithServer)
        const maps = await db.idmap.toArray();
        for (const m of maps) {
            if (m.collection === collection && m.scopeKey === scopeKey) {
                this._sidCache.set(collection + '|' + scopeKey + '|' + m.cid, m.sid);
            }
        }

        // Optionaler Fallback (falls du in Zukunft mal Rows hast,
        // die gleichzeitig clientId+serverId tragen)
        const rows = await db.entities.where('[collection+scopeKey]')
            .equals([collection, scopeKey]).toArray();
        for (const r of rows) {
            if (r.clientId && r.serverId) {
                this._sidCache.set(collection + '|' + scopeKey + '|' + r.clientId, r.serverId);
            }
        }
    }

    async drain() {
        if (this.running || !navigator.onLine) return;
        this.running = true;
        try {
            const items = await outboxRepo.listAll();
            for (const it of items) {
                try {
                    // Meta lesen
                    const metaLight = JSON.parse(it.metaEnc) as { collection: string; scopeKey: string; op: 'UPSERT' | 'DELETE' | 'CUSTOM' | 'PATCH'; extractIdFromUrl?: boolean }

                    // Body & URL mit bekannten ID-Mappings umschreiben
                    const replace = (s: string) => {
                        if (typeof s !== 'string') return s as unknown as string;
                        if (!s.includes('cid:')) return s;
                        // naive Ersetzung: alle cid:XYZ die wir kennen, durch sid ersetzen
                        // (performant genug für URLs und kompakte Bodies)
                        return s.replace(/cid:[a-zA-Z0-9-]+/g, (cid) => {
                            // lookup sid:
                            // Achtung: synchronous Zugriff – wir haben keine Sync-API auf Dexie.
                            // Workaround: wir sammeln später im Body; hier nur URLs ersetzen.
                            return (this._sidCache.get(metaLight.collection + '|' + metaLight.scopeKey + '|' + cid)) ?? cid;
                        });
                    };

                    // lokale SID-Map der Session aufbauen (minimiert Dexie-Reads)
                    await this._hydrateSidCache(metaLight.collection, metaLight.scopeKey);

                    const bodyOriginal = JSON.parse(it.bodyEnc);
                    const bodyRewritten = deepReplaceIds(bodyOriginal, replace);
                    const urlRewritten = replace(it.url);

                    //  Context setzen: Replay true + OUTBOX_ID + volles OFFLINE_META (damit der Interceptor Repo-Sync macht)
                    const ctx = new HttpContext()
                        .set(REPLAYING, true)
                        .set(OUTBOX_ID, it.id)
                        .set(CLIENT_ID, it.clientId ?? null)
                        .set(OFFLINE_META, {
                            kind: 'mutate',
                            collection: metaLight.collection,
                            scopeArgs: _parseScope(it.scopeKey),
                            op: metaLight.op,
                            // wir brauchen hier keine optimisticBody / extractIdFromUrl – Replay ist online
                        } as any);

                    // Senden (ohne persistierte Header; Idempotency-Key nur als Bonus)
                    const headers = new HttpHeaders({ 'X-Idempotency-Key': it.id });
                    await firstValueFrom(
                        this.http.request(it.method, urlRewritten, { body: bodyRewritten, context: ctx, headers })
                    );

                    // Erfolg → Outbox löschen
                    await outboxRepo.remove(it.id);


                    if (bodyOriginal?.id && typeof bodyOriginal.id === 'string' && bodyOriginal.id.startsWith('cid:')) {
                        // Der Interceptor ruft entityRepo.replaceClientWithServer(...) → idmap wird geschrieben.
                    }
                } catch (e) {
                    await outboxRepo.bumpReplay(it.id);
                }
            }
        } finally {
            this.running = false;
        }
    }

    private _sidCache = new Map<string, string>(); // key: collection|scopeKey|cid

}

// Hilfsfunktion: ScopeArgs aus scopeKey zurück (für Replay-Meta)
function _parseScope(scopeKey: string): Record<string, string> | undefined {
    if (!scopeKey) return undefined;
    const parts = scopeKey.split('|');
    const out: Record<string, string> = {};
    for (const p of parts) {
        const i = p.indexOf('=');
        if (i > 0) out[p.slice(0, i)] = decodeURIComponent(p.slice(i + 1));
    }
    return out;
}
